import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ApiUser } from '../api/client';

interface AuthContextValue {
  user: ApiUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (tokens: { accessToken: string; refreshToken: string }, user: ApiUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function loadStoredUser(): ApiUser | null {
  const raw = localStorage.getItem('bankably_user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApiUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(loadStoredUser);

  const login = useCallback((tokens: { accessToken: string; refreshToken: string }, newUser: ApiUser) => {
    localStorage.setItem('bankably_access_token', tokens.accessToken);
    localStorage.setItem('bankably_refresh_token', tokens.refreshToken);
    localStorage.setItem('bankably_user', JSON.stringify(newUser));
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('bankably_access_token');
    localStorage.removeItem('bankably_refresh_token');
    localStorage.removeItem('bankably_user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: !!user?.isAdmin,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
